import type {
  AllMailConversationListItem,
  AllMailFolder,
  ComposeMailInput,
  ConversationListItem,
  MailAttachmentListItem,
  MailAttachmentMeta,
  MailMessage,
  Mailbox,
  MailboxSmtpCredential,
  MailView,
} from '@mevaser/shared'
import { ApiError, MAX_MAIL_ATTACHMENT_BYTES, queryKeys } from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  useToast,
} from '@mevaser/ui'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  Bold,
  Copy,
  Inbox,
  Italic,
  List,
  ListOrdered,
  Layers,
  Mail as MailIcon,
  MailOpen,
  Paperclip,
  Pencil,
  Reply,
  ReplyAll,
  Forward as ForwardIcon,
  Send,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Star,
  Ticket,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AiAssistButton, type AiAssistContext } from '../components/ai-assist.js'
import { Page, PageHeader } from '../components/app-shell.js'
import { BulkActionBar, DataTable, type DataTableProps } from '../components/data-table.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const FOLDERS: { view: MailView; label: string; icon: typeof Inbox }[] = [
  { view: 'inbox', label: 'Inbox', icon: Inbox },
  { view: 'starred', label: 'Starred', icon: Star },
  { view: 'sent', label: 'Sent', icon: Send },
  { view: 'drafts', label: 'Drafts', icon: Pencil },
  { view: 'archive', label: 'Archive', icon: Archive },
  { view: 'junk', label: 'Junk', icon: ShieldAlert },
  { view: 'trash', label: 'Trash', icon: Trash2 },
]

const PAGE_SIZE = 25

const ALL_FOLDER_LABELS: Record<AllMailFolder, string> = {
  inbox: 'Inbox',
  starred: 'Starred',
  sent: 'Sent',
  drafts: 'Drafts',
}

/** A list row; in the combined All mail view it also says which mailbox/folders it is in. */
type ConversationRow = ConversationListItem & Partial<Pick<AllMailConversationListItem, 'mailboxId' | 'mailboxName' | 'folders'>>

function relativeDate(iso: string): string {
  const date = new Date(iso)
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000)
  if (days <= 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (days === 1) return 'Yesterday'
  if (days < 7) return date.toLocaleDateString([], { weekday: 'short' })
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function splitAddresses(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((v) => v.trim())
    .filter(Boolean)
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Whether the viewport is at Tailwind's `lg` breakpoint or wider — tracked in
 * JS, not just CSS, because the thread pane's mobile `<Dialog>` must not
 * *mount* on a large screen at all. `lg:hidden` on the dialog's panel only
 * hides that one element; the Radix overlay it renders alongside is a
 * sibling with no such class, so a `className`-only approach leaves a
 * full-page darkened backdrop on desktop with the real content showing
 * through underneath it (the always-visible third grid column) — the exact
 * bug this hook exists to prevent.
 */
function useIsLargeScreen(): boolean {
  const query = '(min-width: 1024px)'
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return matches
}

// -------------------------------------------------------------- body editor

/**
 * A minimal WYSIWYG body for mail compose — bold, italic, bullet and
 * numbered lists. Deliberately the same scope as `RichTextEditor` (product
 * descriptions): this is the toolbar most people actually reach for day to
 * day, and it reuses the exact same Tiptap setup already in this app rather
 * than adding a second rich-text stack. Link insertion is not in this pass —
 * a pasted URL still renders as plain text, not a clickable link.
 */
function MailBodyEditor({
  value,
  onChange,
  placeholder,
  aiAssist,
}: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  /** Shows the AI writing-help sparkle on the toolbar (t1232). */
  aiAssist?: AiAssistContext
}) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, link: false, underline: false })],
    content: value,
    editorProps: {
      attributes: {
        class: 'rich-text min-h-40 px-3 py-2 text-sm focus:outline-none',
        'data-placeholder': placeholder ?? '',
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
  })

  return (
    <div className="rounded-md border border-border bg-surface text-sm focus-within:border-primary">
      <div className="flex items-center gap-1 border-b border-border px-1.5 py-1">
        <button
          type="button"
          aria-label="Bold"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          className="flex size-6 items-center justify-center rounded text-foreground-muted hover:bg-surface-sunken hover:text-foreground"
        >
          <Bold className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Italic"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          className="flex size-6 items-center justify-center rounded text-foreground-muted hover:bg-surface-sunken hover:text-foreground"
        >
          <Italic className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Bullet list"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          className="flex size-6 items-center justify-center rounded text-foreground-muted hover:bg-surface-sunken hover:text-foreground"
        >
          <List className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Numbered list"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          className="flex size-6 items-center justify-center rounded text-foreground-muted hover:bg-surface-sunken hover:text-foreground"
        >
          <ListOrdered className="size-3.5" />
        </button>
        {aiAssist ? (
          <div className="ms-auto flex items-center gap-1">
            <AiAssistButton
              target="rich-text"
              value={value}
              onApply={(html) => {
                editor?.commands.setContent(html)
                onChange(html)
              }}
              context={aiAssist}
            />
          </div>
        ) : null}
      </div>
      <EditorContent editor={editor} />
    </div>
  )
}

// -------------------------------------------------------------- message body

/**
 * An `<iframe>` clips its own content by default, but the *outer* layout
 * does not know that — a real inbound email routinely carries a fixed-width
 * table or an unwrapped long link, and without `min-w-0` on every flex/grid
 * ancestor between here and the page root, that content's intrinsic width
 * leaks out and grows the whole pane instead of scrolling inside its box.
 * The CSS reset below constrains the email's own content (images/tables
 * can't exceed the iframe's width); `min-w-0` here is the matching half of
 * that fix on the React side.
 */
const EMAIL_BODY_RESET = `<style>
  html, body { margin: 0; padding: 8px; overflow-wrap: anywhere; word-break: break-word; }
  img, table { max-width: 100% !important; height: auto !important; }
  pre { white-space: pre-wrap; }
</style>`

function MessageBody({ message }: { message: MailMessage }) {
  if (message.htmlBody) {
    // `sandbox=""` (no scripts, no forms, no top navigation) is the hard
    // boundary; the body itself was already allowlist-sanitized server-side
    // (on ingest, or on send) — belt and braces, not either alone.
    return (
      <iframe
        title="Message body"
        sandbox=""
        srcDoc={EMAIL_BODY_RESET + message.htmlBody}
        className="w-full min-w-0 rounded-md border border-border bg-white"
        style={{ height: 380 }}
      />
    )
  }
  return (
    <pre className="min-w-0 overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border bg-surface p-3 text-sm">
      {message.textBody || '(no content)'}
    </pre>
  )
}

// -------------------------------------------------------------- compose

interface ComposeState {
  mailboxId: string
  to: string
  cc: string
  subject: string
  html: string
  inReplyToMessageId?: string
  forwardOfMessageId?: string
  /** Set to resume an existing saved draft instead of composing a new message. */
  draftId?: string
  attachments?: MailAttachmentMeta[]
  /** The thread being replied to, for AI reply drafts (t1232). Never sent to the mail API. */
  conversation?: AiAssistContext['conversation']
}

/** Seeds the compose window from a saved draft message — its mailbox,
 *  recipients, subject, body, and attachments, plus the draft's own id so
 *  Save/Send edit that row in place instead of creating a new one. No
 *  threading fields: the draft row already carries its own threading
 *  server-side and `PATCH /mail/messages/:id` doesn't accept them. No bcc
 *  either — this compose UI has no bcc field, and omitting it from the PATCH
 *  leaves whatever bcc the draft already has untouched. */
function draftToComposeState(message: MailMessage): ComposeState {
  return {
    mailboxId: message.mailboxId,
    to: message.toAddresses.join(', '),
    cc: message.ccAddresses.join(', '),
    subject: message.subject,
    html: message.htmlBody ?? message.textBody ?? '',
    draftId: message.id,
    attachments: message.attachments,
  }
}

function ComposeDialog({
  open,
  onOpenChange,
  mailboxes,
  initial,
  onSent,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mailboxes: Mailbox[]
  initial: ComposeState | null
  onSent: () => void
}) {
  const toast = useToast()
  const [state, setState] = useState<ComposeState>(
    initial ?? { mailboxId: mailboxes[0]?.id ?? '', to: '', cc: '', subject: '', html: '' },
  )
  const [showCc, setShowCc] = useState(Boolean(initial?.cc))
  // Set when resuming a saved draft (seeded from `initial.draftId`), or the
  // moment any file is attached during a fresh compose — from then on,
  // Send/Save both go through this same draft row (PATCH + send) instead of
  // the one-shot compose call, since an attachment has to live somewhere
  // before the message exists. A plain send with no attachments never
  // creates one.
  const [draftId, setDraftId] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<MailAttachmentMeta[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      const seeded = initial ?? { mailboxId: mailboxes[0]?.id ?? '', to: '', cc: '', subject: '', html: '' }
      setState(seeded)
      setShowCc(Boolean(seeded.cc))
      setDraftId(seeded.draftId ?? null)
      setAttachments(seeded.attachments ?? [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial])

  async function ensureDraft(): Promise<string> {
    if (draftId) return draftId
    const body: ComposeMailInput = {
      mailboxId: state.mailboxId,
      to: splitAddresses(state.to),
      cc: splitAddresses(state.cc),
      bcc: [],
      subject: state.subject,
      html: state.html || null,
      text: null,
      inReplyToMessageId: state.inReplyToMessageId,
      forwardOfMessageId: state.forwardOfMessageId,
      draft: true,
    }
    const created = await api.post<MailMessage>('/mail/messages', body)
    setDraftId(created.id)
    // A forward's own attachments are copied onto the draft server-side the
    // moment it exists — pick them up now so they show alongside anything
    // about to be attached by hand.
    setAttachments(created.attachments)
    return created.id
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_MAIL_ATTACHMENT_BYTES) {
          toast.error('Could not attach', `${file.name} is larger than 10 MB.`)
          continue
        }
        const id = await ensureDraft()
        const formData = new FormData()
        formData.append('file', file)
        const attachment = await api.postFile<MailAttachmentMeta>(
          `/mail/messages/${id}/attachments`,
          formData,
        )
        setAttachments((prev) => [...prev, attachment])
      }
    } catch (error) {
      toast.error('Could not attach the file', error instanceof ApiError ? error.message : undefined)
    } finally {
      setUploading(false)
    }
  }

  async function removeAttachment(attachmentId: string) {
    try {
      await api.delete(`/mail/attachments/${attachmentId}`)
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
    } catch (error) {
      toast.error('Could not remove the attachment', error instanceof ApiError ? error.message : undefined)
    }
  }

  const send = useMutation({
    mutationFn: async () => {
      if (draftId) {
        await api.patch(`/mail/messages/${draftId}`, {
          to: splitAddresses(state.to),
          cc: splitAddresses(state.cc),
          subject: state.subject,
          html: state.html || null,
        })
        return api.post<MailMessage>(`/mail/messages/${draftId}/send`, {})
      }
      const body: ComposeMailInput = {
        mailboxId: state.mailboxId,
        to: splitAddresses(state.to),
        cc: splitAddresses(state.cc),
        bcc: [],
        subject: state.subject,
        html: state.html || null,
        text: null,
        inReplyToMessageId: state.inReplyToMessageId,
        forwardOfMessageId: state.forwardOfMessageId,
        draft: false,
      }
      return api.post<MailMessage>('/mail/messages', body)
    },
    onSuccess: () => {
      toast.success('Sent')
      onOpenChange(false)
      onSent()
    },
    onError: (error) => toast.error('Could not send', error instanceof ApiError ? error.message : undefined),
  })

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (draftId) {
        return api.patch<MailMessage>(`/mail/messages/${draftId}`, {
          to: splitAddresses(state.to),
          cc: splitAddresses(state.cc),
          subject: state.subject,
          html: state.html || null,
        })
      }
      const body: ComposeMailInput = {
        mailboxId: state.mailboxId,
        to: splitAddresses(state.to),
        cc: splitAddresses(state.cc),
        bcc: [],
        subject: state.subject,
        html: state.html || null,
        text: null,
        inReplyToMessageId: state.inReplyToMessageId,
        forwardOfMessageId: state.forwardOfMessageId,
        draft: true,
      }
      return api.post<MailMessage>('/mail/messages', body)
    },
    onSuccess: () => {
      toast.success('Draft saved')
      onOpenChange(false)
      onSent()
    },
    onError: (error) => toast.error('Could not save the draft', error instanceof ApiError ? error.message : undefined),
  })

  const canSend = state.mailboxId && splitAddresses(state.to).length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {state.draftId
              ? 'Edit draft'
              : state.forwardOfMessageId
                ? 'Forward'
                : state.inReplyToMessageId
                  ? 'Reply'
                  : 'New message'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="From">
            {(props) => (
              <Select
                value={state.mailboxId}
                onValueChange={(v) => setState((s) => ({ ...s, mailboxId: v }))}
                disabled={Boolean(draftId)}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {mailboxes.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.displayName} &lt;{m.address}&gt;
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Field label="To">
                {(props) => (
                  <Input
                    {...props}
                    value={state.to}
                    placeholder="name@example.com, another@example.com"
                    onChange={(e) => setState((s) => ({ ...s, to: e.target.value }))}
                  />
                )}
              </Field>
            </div>
            {!showCc ? (
              <Button variant="ghost" size="sm" className="mt-6" onClick={() => setShowCc(true)}>
                Cc
              </Button>
            ) : null}
          </div>

          {showCc ? (
            <Field label="Cc">
              {(props) => (
                <Input
                  {...props}
                  value={state.cc}
                  onChange={(e) => setState((s) => ({ ...s, cc: e.target.value }))}
                />
              )}
            </Field>
          ) : null}

          <Field label="Subject">
            {(props) => (
              <Input
                {...props}
                value={state.subject}
                onChange={(e) => setState((s) => ({ ...s, subject: e.target.value }))}
              />
            )}
          </Field>

          <Field label="Message">
            {() => (
              <MailBodyEditor
                value={state.html}
                onChange={(html) => setState((s) => ({ ...s, html }))}
                placeholder="Write your message…"
                aiAssist={{
                  fieldLabel: state.conversation?.length ? 'email reply' : 'email',
                  hints: {
                    ...(state.subject.trim() ? { Subject: state.subject.slice(0, 500) } : {}),
                    ...(state.to.trim() ? { To: state.to.slice(0, 500) } : {}),
                  },
                  conversation: state.conversation,
                }}
              />
            )}
          </Field>

          {attachments.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {attachments.map((att) => (
                <span
                  key={att.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-sunken px-2 py-1 text-xs"
                >
                  <Paperclip className="size-3 text-foreground-muted" />
                  {att.filename}
                  <span className="text-foreground-muted">({formatBytes(att.sizeBytes)})</span>
                  <button
                    type="button"
                    aria-label={`Remove ${att.filename}`}
                    onClick={() => removeAttachment(att.id)}
                    className="text-foreground-muted hover:text-danger"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              void handleFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="me-auto"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="size-4" /> {uploading ? 'Attaching…' : 'Attach'}
          </Button>
          <Button variant="ghost" onClick={() => saveDraft.mutate()} disabled={saveDraft.isPending}>
            Save draft
          </Button>
          <Button variant="primary" onClick={() => send.mutate()} disabled={!canSend || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// -------------------------------------------------------------- manage mailboxes

function ManageMailboxesDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [address, setAddress] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [deleting, setDeleting] = useState<Mailbox | null>(null)
  const [settingsId, setSettingsId] = useState<string | null>(null)

  // Its own list: the page's list is active-only, but managing has to show
  // inactive mailboxes too or there would be no way to reactivate one.
  const { data: mailboxes } = useQuery({
    queryKey: queryKeys.mail.mailboxesAll(),
    queryFn: () => api.get<Mailbox[]>('/mail/mailboxes', { query: { includeInactive: true } }),
    enabled: open,
  })

  const { data: staff } = useQuery({
    queryKey: ['staff', 'list-for-mail'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/staff'),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: () =>
      api.post('/mail/mailboxes', { address, displayName, userId: null, signature: null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mail.mailboxes() })
      setAddress('')
      setDisplayName('')
      toast.success('Mailbox created')
    },
    onError: (error) => toast.error('Could not create the mailbox', error instanceof ApiError ? error.message : undefined),
  })

  const assign = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      api.patch(`/mail/mailboxes/${id}`, { userId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mail.mailboxes() })
    },
    onError: (error) => toast.error('Could not update the mailbox', error instanceof ApiError ? error.message : undefined),
  })

  const toggleActive = useMutation({
    mutationFn: (mailbox: Mailbox) => api.patch(`/mail/mailboxes/${mailbox.id}`, { active: !mailbox.active }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mail.all() })
    },
    onError: (error) => toast.error('Could not update the mailbox', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage mailboxes</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            {(mailboxes ?? []).map((mailbox) => (
              <div
                key={mailbox.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{mailbox.displayName}</p>
                    {!mailbox.active ? <Badge tone="danger">Inactive</Badge> : null}
                  </div>
                  <p className="truncate text-xs text-foreground-muted">{mailbox.address}</p>
                </div>
                <Select
                  value={mailbox.userId ?? 'shared'}
                  onValueChange={(v) => assign.mutate({ id: mailbox.id, userId: v === 'shared' ? null : v })}
                >
                  <SelectTrigger className="w-40 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Shared</SelectItem>
                    {(staff ?? []).map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleActive.mutate(mailbox)}
                  disabled={toggleActive.isPending}
                >
                  {mailbox.active ? 'Deactivate' : 'Reactivate'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Outlook and Gmail access"
                  aria-label={`Outlook and Gmail access for ${mailbox.address}`}
                  onClick={() => setSettingsId(mailbox.id)}
                >
                  <Settings2 className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Delete mailbox"
                  aria-label={`Delete ${mailbox.address}`}
                  onClick={() => setDeleting(mailbox)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium">Add a mailbox</p>
            <Field label="Address">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={address}
                  placeholder="info@mevasertech.net"
                  onChange={(e) => setAddress(e.target.value)}
                />
              )}
            </Field>
            <Field label="Display name">
              {(props) => (
                <Input
                  {...props}
                  value={displayName}
                  placeholder="Info / front desk"
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              )}
            </Field>
            <Button
              variant="secondary"
              onClick={() => create.mutate()}
              disabled={!address || !displayName || create.isPending}
            >
              Add mailbox
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <MailboxAccessDialog
      mailbox={(mailboxes ?? []).find((m) => m.id === settingsId) ?? null}
      onClose={() => setSettingsId(null)}
    />
    <DeleteMailboxDialog mailbox={deleting} onClose={() => setDeleting(null)} />
    </>
  )
}

/** Gets a mailbox's mail into, and out of, an outside client such as Gmail or
 *  Outlook: inbound copies are forwarded to an address of the admin's choosing,
 *  and a Mailgun SMTP login lets that client send as the mailbox address. */
function MailboxAccessDialog({ mailbox, onClose }: { mailbox: Mailbox | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [forwardTo, setForwardTo] = useState('')
  const [forwardOn, setForwardOn] = useState(false)
  // Shown once: the server keeps no copy of the password.
  const [credential, setCredential] = useState<MailboxSmtpCredential | null>(null)

  const mailboxId = mailbox?.id
  const savedAddress = mailbox?.forwardingAddress
  const savedEnabled = mailbox?.forwardingEnabled
  useEffect(() => {
    setForwardTo(savedAddress ?? '')
    setForwardOn(savedEnabled ?? false)
  }, [mailboxId, savedAddress, savedEnabled])
  useEffect(() => {
    setCredential(null)
  }, [mailboxId])

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.mail.all() })
  const failed = (title: string) => (error: unknown) =>
    toast.error(title, error instanceof ApiError ? error.message : undefined)

  const saveForwarding = useMutation({
    mutationFn: () =>
      api.patch(`/mail/mailboxes/${mailboxId}`, {
        forwardingAddress: forwardTo.trim() || null,
        forwardingEnabled: forwardOn && forwardTo.trim() !== '',
      }),
    onSuccess: async () => {
      await refresh()
      toast.success('Forwarding saved')
    },
    onError: failed('Could not save forwarding'),
  })

  const createLogin = useMutation({
    mutationFn: () => api.post<MailboxSmtpCredential>(`/mail/mailboxes/${mailboxId}/smtp-credential`, {}),
    onSuccess: async (created) => {
      setCredential(created)
      await refresh()
    },
    onError: failed('Could not create the sending password'),
  })

  const revokeLogin = useMutation({
    mutationFn: () => api.delete(`/mail/mailboxes/${mailboxId}/smtp-credential`),
    onSuccess: async () => {
      setCredential(null)
      await refresh()
      toast.success('Sending password cancelled')
    },
    onError: failed('Could not cancel the sending password'),
  })

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('Copied')
    } catch {
      toast.error('Could not copy — select the text and copy it by hand')
    }
  }

  const hasLogin = Boolean(mailbox?.smtpCredentialCreatedAt)
  const dirty = forwardTo.trim() !== (savedAddress ?? '') || forwardOn !== (savedEnabled ?? false)

  return (
    <Dialog open={mailbox !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Outlook and Gmail access — {mailbox?.address}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-6">
          <section className="space-y-3">
            <p className="text-sm font-medium">Receive in another inbox</p>
            <p className="text-xs text-foreground-muted">
              Every email that arrives in this mailbox is also copied to the address below, for example your Gmail.
              The portal keeps its own copy. If you reply from there, the reply goes straight to the original sender.
            </p>
            <Field label="Copy incoming mail to">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={forwardTo}
                  placeholder="you@gmail.com"
                  onChange={(e) => setForwardTo(e.target.value)}
                />
              )}
            </Field>
            <div className="flex items-center justify-between rounded-md border border-border p-2.5">
              <span className="text-sm">Forwarding is on</span>
              <Switch checked={forwardOn} onCheckedChange={setForwardOn} />
            </div>
            <Button
              variant="secondary"
              onClick={() => saveForwarding.mutate()}
              disabled={!dirty || saveForwarding.isPending}
            >
              {saveForwarding.isPending ? 'Saving…' : 'Save forwarding'}
            </Button>
          </section>

          <section className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium">Send from Gmail or Outlook</p>
            <p className="text-xs text-foreground-muted">
              {hasLogin
                ? `A sending password has been active since ${new Date(mailbox!.smtpCredentialCreatedAt!).toLocaleDateString()}. Creating a new one replaces it, and the old one stops working.`
                : 'No sending password yet. Create one to let Gmail or Outlook send mail as this address.'}
            </p>

            {credential ? (
              <Alert tone="warning" title="Copy these now — the password is shown only once">
                <dl className="mt-2 space-y-1.5 text-sm">
                  {(
                    [
                      ['Server', credential.smtpHost],
                      ['Port', String(credential.smtpPort)],
                      ['Username', credential.username],
                      ['Password', credential.password],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-2">
                      <dt className="w-20 shrink-0">{label}</dt>
                      <dd className="min-w-0 flex-1 truncate font-mono text-xs">{value}</dd>
                      <Button variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={() => copy(value)}>
                        <Copy className="size-4" />
                      </Button>
                    </div>
                  ))}
                </dl>
              </Alert>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => createLogin.mutate()} disabled={createLogin.isPending}>
                {createLogin.isPending ? 'Working…' : hasLogin ? 'Reset sending password' : 'Create sending password'}
              </Button>
              {hasLogin ? (
                <Button variant="ghost" onClick={() => revokeLogin.mutate()} disabled={revokeLogin.isPending}>
                  Cancel sending password
                </Button>
              ) : null}
            </div>

            <div className="space-y-1 text-xs text-foreground-muted">
              <p className="font-medium text-foreground">Setting it up in Gmail</p>
              <ol className="list-decimal space-y-0.5 pl-5">
                <li>On a computer, open Gmail → Settings → See all settings → Accounts and Import.</li>
                <li>Under “Send mail as”, choose “Add another email address” and enter {mailbox?.address}.</li>
                <li>Enter the server, port 587, username and password above, and choose a secured (TLS) connection.</li>
                <li>
                  Gmail emails a confirmation code to {mailbox?.address}. It arrives in this portal mailbox, and in
                  your other inbox too if forwarding is on.
                </li>
                <li>After that, the Gmail app lets you pick {mailbox?.address} in the “From” line when writing.</li>
              </ol>
              <p>
                Mail sent this way does not appear in this portal’s Sent folder. Outlook can receive the forwarded
                copies, but cannot sign in to the portal mailbox as its own account.
              </p>
            </div>
          </section>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteMailboxDialog({ mailbox, onClose }: { mailbox: Mailbox | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.delete(`/mail/mailboxes/${mailbox?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mail.all() })
      toast.success('Mailbox deleted')
      onClose()
    },
    onError: (error) => toast.error('Could not delete the mailbox', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={mailbox !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {mailbox?.address}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm">
            This permanently deletes the mailbox and every message and attachment in it, for everyone. This cannot
            be undone. To keep the history and just stop using the mailbox, deactivate it instead.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// -------------------------------------------------------------- thread pane

function ThreadPane({
  mailboxId,
  threadId,
  mailboxes,
  onReply,
}: {
  mailboxId: string
  threadId: string
  mailboxes: Mailbox[]
  onReply: (state: ComposeState) => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { can } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.mail.thread(mailboxId, threadId),
    queryFn: () =>
      api.get<{ threadId: string; messages: MailMessage[] }>(
        `/mail/mailboxes/${mailboxId}/threads/${encodeURIComponent(threadId)}`,
      ),
  })

  const flag = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, boolean> }) =>
      api.patch(`/mail/messages/${id}/flags`, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mail'] })
    },
    onError: (error) => toast.error('Could not update the message', error instanceof ApiError ? error.message : undefined),
  })

  const junk = useMutation({
    mutationFn: (action: 'junk' | 'unjunk') =>
      api.post(`/mail/mailboxes/${mailboxId}/bulk`, { threadIds: [threadId], action }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mail'] })
    },
    onError: (error) => toast.error('Could not update the conversation', error instanceof ApiError ? error.message : undefined),
  })

  const moveToInbox = useMutation({
    mutationFn: () => api.post(`/mail/mailboxes/${mailboxId}/bulk`, { threadIds: [threadId], action: 'inbox' }),
    onSuccess: async () => {
      toast.success('Moved to Inbox')
      await queryClient.invalidateQueries({ queryKey: ['mail'] })
    },
    onError: (error) => toast.error('Could not move the conversation', error instanceof ApiError ? error.message : undefined),
  })

  const createTicket = useMutation({
    mutationFn: (mailMessageId: string) =>
      api.post<{ ticketId: string; created: boolean }>('/support-tickets/from-email', {
        mailMessageId,
      }),
    onSuccess: async (result) => {
      toast.success(result.created ? 'Support ticket created' : 'Added to the existing ticket')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mail'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() }),
      ])
    },
    onError: (error) =>
      toast.error('Could not create the ticket', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-32" />
      </div>
    )
  }
  if (!data) return null

  const mailbox = mailboxes.find((m) => m.id === mailboxId)
  const lastInbound = [...data.messages].reverse().find((m) => m.direction === 'inbound')
  const last = data.messages[data.messages.length - 1]

  // The thread so far, for AI reply drafts (t1232): drafts are skipped, HTML is
  // flattened to text, and only the last 20 messages are sent.
  const conversation: NonNullable<ComposeState['conversation']> = data.messages
    .filter((m) => m.status !== 'draft')
    .flatMap((m) => {
      const text = (
        m.textBody ??
        (m.htmlBody ? m.htmlBody.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ') : '')
      )
        .trim()
        .slice(0, 4000)
      const from = m.direction === 'inbound' ? ('customer' as const) : ('staff' as const)
      return text ? [{ from, text }] : []
    })
    .slice(-20)

  const replyTo = () => {
    if (!last) return
    onReply({
      mailboxId,
      to: last.direction === 'inbound' ? last.fromAddress : last.toAddresses.join(', '),
      cc: '',
      subject: last.subject.startsWith('Re:') ? last.subject : `Re: ${last.subject}`,
      html: '',
      inReplyToMessageId: last.id,
      conversation,
    })
  }

  const replyAll = () => {
    if (!last || !mailbox) return
    const recipients = new Set(
      [last.fromAddress, ...last.toAddresses, ...last.ccAddresses].filter(
        (a) => a.toLowerCase() !== mailbox.address.toLowerCase(),
      ),
    )
    onReply({
      mailboxId,
      to: [...recipients].join(', '),
      cc: '',
      subject: last.subject.startsWith('Re:') ? last.subject : `Re: ${last.subject}`,
      html: '',
      inReplyToMessageId: last.id,
      conversation,
    })
  }

  const forward = () => {
    if (!last) return
    onReply({
      mailboxId,
      to: '',
      cc: '',
      subject: last.subject.startsWith('Fwd:') ? last.subject : `Fwd: ${last.subject}`,
      html: `<p></p><blockquote>${last.htmlBody ?? `<p>${last.textBody ?? ''}</p>`}</blockquote>`,
      forwardOfMessageId: last.id,
    })
  }

  return (
    <div className="flex h-full min-w-0 flex-col overflow-y-auto p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="truncate text-lg font-semibold">{last?.subject || '(no subject)'}</h2>
        <div className="flex shrink-0 items-center gap-1">
          {data.messages.some((m) => m.archivedAt || m.trashedAt || m.junkedAt) ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={moveToInbox.isPending}
              onClick={() => moveToInbox.mutate()}
            >
              <Inbox className="size-4" /> Move to inbox
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            aria-label={lastInbound?.starred ? 'Unstar' : 'Star'}
            onClick={() => lastInbound && flag.mutate({ id: lastInbound.id, patch: { starred: !lastInbound.starred } })}
          >
            <Star className={cn('size-4', lastInbound?.starred && 'fill-warning text-warning')} />
          </Button>
          {data.messages.some((m) => m.archivedAt) ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Unarchive"
              onClick={() => last && flag.mutate({ id: last.id, patch: { archived: false } })}
            >
              <ArchiveRestore className="size-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Archive"
              onClick={() => last && flag.mutate({ id: last.id, patch: { archived: true } })}
            >
              <Archive className="size-4" />
            </Button>
          )}
          {lastInbound ? (
            data.messages.some((m) => m.junkedAt) ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Not junk"
                title="Not junk"
                onClick={() => junk.mutate('unjunk')}
              >
                <ShieldCheck className="size-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Mark as junk"
                title="Mark as junk"
                onClick={() => junk.mutate('junk')}
              >
                <ShieldAlert className="size-4" />
              </Button>
            )
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Trash"
            onClick={() => last && flag.mutate({ id: last.id, patch: { trashed: true } })}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {data.messages.map((message) => (
          <div key={message.id} className="rounded-lg border border-border p-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{message.fromAddress}</p>
                <p className="truncate text-xs text-foreground-muted">
                  to {message.toAddresses.join(', ')}
                  {message.ccAddresses.length > 0 ? ` · cc ${message.ccAddresses.join(', ')}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {message.status === 'draft' ? <Badge tone="neutral">Draft</Badge> : null}
                {message.status === 'failed' ? <Badge tone="danger">Failed to send</Badge> : null}
                {message.status === 'queued' ? <Badge tone="info">Sending…</Badge> : null}
                {message.direction === 'inbound' && message.supportTicketId ? (
                  <Link
                    to="/support-tickets/$ticketId"
                    params={{ ticketId: message.supportTicketId }}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <Ticket className="size-3.5" /> View ticket
                  </Link>
                ) : message.direction === 'inbound' && can('support:write') ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={createTicket.isPending}
                    onClick={() => createTicket.mutate(message.id)}
                  >
                    <Ticket className="size-4" /> Create ticket
                  </Button>
                ) : null}
                <span className="text-xs text-foreground-muted tabular">
                  {relativeDate(message.createdAt)}
                </span>
                {message.status === 'draft' ? (
                  <Button size="sm" variant="secondary" onClick={() => onReply(draftToComposeState(message))}>
                    <Pencil className="size-4" /> Edit draft
                  </Button>
                ) : null}
              </div>
            </div>
            {message.errorMessage ? <Alert tone="danger">{message.errorMessage}</Alert> : null}
            <MessageBody message={message} />
            {message.attachments.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.attachments.map((att) => (
                  <a
                    key={att.id}
                    href={`${apiBaseUrl}/mail/attachments/${att.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground-muted hover:text-foreground"
                  >
                    <Paperclip className="size-3" />
                    {att.filename}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 mt-4 flex gap-2 border-t border-border bg-surface pt-3">
        {last?.status === 'draft' ? (
          // Replying to a message that hasn't been sent yet makes no sense —
          // resume and finish it instead.
          <Button variant="primary" onClick={() => onReply(draftToComposeState(last))}>
            <Pencil className="size-4" /> Edit draft
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={replyTo}>
              <Reply className="size-4" /> Reply
            </Button>
            <Button variant="secondary" onClick={replyAll}>
              <ReplyAll className="size-4" /> Reply all
            </Button>
            <Button variant="ghost" onClick={forward}>
              <ForwardIcon className="size-4" /> Forward
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// -------------------------------------------------------------- attachments tab

/**
 * Every inbound attachment in the mailbox, independent of which folder its
 * message sits in — a signed contract someone archived, or a photo buried in
 * an old thread, is still one search away. Clicking a row opens the
 * conversation it came from (where the file can actually be downloaded),
 * rather than duplicating a download link here.
 */
function AttachmentsPanel({
  mailboxId,
  onOpenThread,
}: {
  mailboxId: string
  onOpenThread: (threadId: string) => void
}) {
  const [q, setQ] = useState('')

  const filters = { q: q || undefined, pageSize: PAGE_SIZE }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.mail.attachments(mailboxId, filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: MailAttachmentListItem[]; total: number; page: number; pageSize: number }>(
        `/mail/mailboxes/${mailboxId}/attachments`,
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns: DataTableProps<MailAttachmentListItem>['columns'] = useMemo(
    () => [
      {
        id: 'filename',
        header: 'File',
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <Paperclip className="size-3.5 shrink-0 text-foreground-muted" />
            <span className="truncate font-medium">{row.original.filename}</span>
            <span className="shrink-0 text-xs text-foreground-muted tabular">
              {formatBytes(row.original.sizeBytes)}
            </span>
          </span>
        ),
      },
      {
        id: 'from',
        header: 'From',
        cell: ({ row }) => <span className="truncate">{row.original.fromAddress}</span>,
      },
      {
        id: 'subject',
        header: 'In conversation',
        cell: ({ row }) => (
          <span className="truncate text-foreground-muted">
            {row.original.subject || '(no subject)'}
          </span>
        ),
      },
      {
        id: 'date',
        header: '',
        size: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-foreground-muted tabular">
            {relativeDate(row.original.createdAt)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="min-w-0 space-y-3">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search attachments by file name, sender or subject…"
      />

      <DataTable
        data={items}
        columns={columns}
        isLoading={isLoading}
        empty={
          <EmptyState
            icon={Paperclip}
            title="No attachments"
            description="Files received in this mailbox will show up here."
          />
        }
        onRowClick={(row) => onOpenThread(row.threadId)}
        getRowId={(row) => row.id}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
      />
    </div>
  )
}

// -------------------------------------------------------------- page

export function MailPage() {
  const { can, staff } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [mailboxId, setMailboxId] = useState<string | null>(null)
  const [section, setSection] = useState<'folders' | 'attachments'>('folders')
  const [view, setView] = useState<MailView | 'all'>('inbox')
  // Only set when a row from the combined view is open; otherwise the selected mailbox applies.
  const [threadMailboxOverride, setThreadMailboxOverride] = useState<string | null>(null)
  const threadMailboxId = threadMailboxOverride ?? mailboxId
  const [search, setSearch] = useState('')
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set())
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeInitial, setComposeInitial] = useState<ComposeState | null>(null)
  const [manageOpen, setManageOpen] = useState(false)
  const isLargeScreen = useIsLargeScreen()

  const { data: mailboxes, isLoading: mailboxesLoading } = useQuery({
    queryKey: queryKeys.mail.mailboxes(),
    queryFn: () => api.get<Mailbox[]>('/mail/mailboxes'),
  })

  useEffect(() => {
    if (!mailboxes) return
    // The selected mailbox can disappear underneath us (deactivated or deleted
    // from Manage mailboxes), so re-pick rather than only filling an empty selection.
    if (mailboxId && mailboxes.some((m) => m.id === mailboxId)) return
    const own = mailboxes.find((m) => m.userId === staff?.id)
    setMailboxId((own ?? mailboxes[0])?.id ?? null)
    setSelectedThreadId(null)
  }, [mailboxes, mailboxId, staff])

  /** A conversation that's nothing but a saved draft should reopen straight
   *  into the compose window, per the client's expectation — not the
   *  read-only thread pane. Anything that isn't a clean single-draft
   *  conversation (no send permission, more than one message, the draft
   *  vanished, a network hiccup) falls back to the normal thread view. */
  async function openDraft(threadId: string, draftMailboxId: string | null) {
    if (!draftMailboxId) return
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.mail.thread(draftMailboxId, threadId),
        queryFn: () =>
          api.get<{ threadId: string; messages: MailMessage[] }>(
            `/mail/mailboxes/${draftMailboxId}/threads/${encodeURIComponent(threadId)}`,
          ),
      })
      const draft = [...data.messages].reverse().find((m) => m.status === 'draft')
      if (!draft) {
        setSelectedThreadId(threadId)
        return
      }
      setComposeInitial(draftToComposeState(draft))
      setComposeOpen(true)
    } catch {
      setSelectedThreadId(threadId)
    }
  }

  const isAll = view === 'all'
  const filters = { view, q: search || undefined, pageSize: PAGE_SIZE }
  const {
    data: conversationPages,
    isLoading: conversationsLoading,
    fetchNextPage: fetchNextConversations,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isFetchingMoreConversations,
  } = useInfiniteQuery({
    queryKey: queryKeys.mail.conversations(isAll ? 'all' : (mailboxId ?? ''), filters),
    queryFn: ({ pageParam }) =>
      isAll
        ? api.get<{ items: ConversationRow[]; total: number; page: number; pageSize: number }>(
            '/mail/conversations',
            { query: { q: filters.q, pageSize: filters.pageSize, page: pageParam } },
          )
        : api.get<{ items: ConversationRow[]; total: number; page: number; pageSize: number }>(
            `/mail/mailboxes/${mailboxId}/conversations`,
            { query: { ...filters, page: pageParam } },
          ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled: (isAll || Boolean(mailboxId)) && section === 'folders',
  })
  const conversationItems = conversationPages?.pages.flatMap((p) => p.items) ?? []
  const conversationTotal = conversationPages?.pages[0]?.total ?? 0

  const bulk = useMutation({
    mutationFn: async (action: string) => {
      if (!isAll) {
        await api.post(`/mail/mailboxes/${mailboxId}/bulk`, { threadIds: [...selectedThreadIds], action })
        return
      }
      // All mail rows are keyed `mailboxId:threadId` (a mailbox id has no colon);
      // the bulk route is per mailbox, so send one request per mailbox.
      const byMailbox = new Map<string, string[]>()
      for (const key of selectedThreadIds) {
        const i = key.indexOf(':')
        const box = key.slice(0, i)
        byMailbox.set(box, [...(byMailbox.get(box) ?? []), key.slice(i + 1)])
      }
      await Promise.all(
        [...byMailbox].map(([box, threadIds]) => api.post(`/mail/mailboxes/${box}/bulk`, { threadIds, action })),
      )
    },
    onSuccess: async () => {
      setSelectedThreadIds(new Set())
      await queryClient.invalidateQueries({ queryKey: ['mail'] })
    },
    onError: (error) => toast.error('Could not update those conversations', error instanceof ApiError ? error.message : undefined),
  })

  const columns: DataTableProps<ConversationRow>['columns'] = useMemo(
    () => [
      {
        id: 'star',
        header: '',
        size: 32,
        cell: ({ row }) => (row.original.starred ? <Star className="size-3.5 fill-warning text-warning" /> : null),
      },
      {
        id: 'participants',
        header: 'From',
        cell: ({ row }) => (
          <span className={cn('truncate', row.original.unread && 'font-semibold')}>
            {row.original.participants.join(', ') || '(unknown)'}
          </span>
        ),
      },
      {
        id: 'subject',
        header: 'Subject',
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className={cn('truncate', row.original.unread && 'font-semibold')}>
              {row.original.subject || '(no subject)'}
            </span>
            <span className="truncate text-foreground-muted">— {row.original.snippet}</span>
            {row.original.hasAttachments ? <Paperclip className="size-3 shrink-0 text-foreground-muted" /> : null}
            {row.original.hasDraft ? <Badge tone="neutral">Draft</Badge> : null}
            {row.original.lastStatus === 'queued' ? <Badge tone="info">Sending…</Badge> : null}
            {row.original.lastStatus === 'failed' ? <Badge tone="danger">Failed to send</Badge> : null}
          </span>
        ),
      },
      ...(isAll
        ? [
            {
              id: 'mailbox',
              header: 'Mailbox',
              size: 180,
              cell: ({ row }: { row: { original: ConversationRow } }) => (
                <span className="flex min-w-0 flex-wrap items-center gap-1">
                  <span className="truncate text-xs">{row.original.mailboxName}</span>
                  {(row.original.folders ?? []).map((f) => (
                    <Badge key={f} tone="neutral">{ALL_FOLDER_LABELS[f]}</Badge>
                  ))}
                </span>
              ),
            },
          ]
        : []),
      {
        id: 'lastAt',
        header: '',
        size: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-foreground-muted tabular">
            {relativeDate(row.original.lastAt)}
          </span>
        ),
      },
    ],
    [isAll],
  )

  if (mailboxesLoading) {
    return (
      <Page>
        <PageHeader title="Mail" />
        <Skeleton className="h-96" />
      </Page>
    )
  }

  if (!mailboxes || mailboxes.length === 0) {
    return (
      <Page>
        <PageHeader
          title="Mail"
          actions={
            can('mail:manage') ? (
              <Button variant="primary" onClick={() => setManageOpen(true)}>
                <Settings2 className="size-4" /> Add a mailbox
              </Button>
            ) : undefined
          }
        />
        <EmptyState
          icon={MailIcon}
          title="No mailboxes yet"
          description={
            can('mail:manage')
              ? 'Add one to start reading and sending mail from the portal.'
              : 'Ask an administrator to set one up.'
          }
        />
        {can('mail:manage') ? (
          <ManageMailboxesDialog open={manageOpen} onOpenChange={setManageOpen} />
        ) : null}
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Mail"
        actions={
          <>
            {can('mail:manage') ? (
              <Button variant="secondary" onClick={() => setManageOpen(true)}>
                <Settings2 className="size-4" /> Manage mailboxes
              </Button>
            ) : null}
            {can('mail:send') ? (
              <Button
                variant="primary"
                onClick={() => {
                  setComposeInitial(mailboxId ? { mailboxId, to: '', cc: '', subject: '', html: '' } : null)
                  setComposeOpen(true)
                }}
              >
                <Pencil className="size-4" /> Compose
              </Button>
            ) : null}
          </>
        }
      />

      {/* `minmax(0,1fr)`, not bare `1fr` — a plain `1fr` track still carries an
          implicit `min-width: auto`, so wide content in a pane (the message
          iframe below, a long subject line) grows that column past its share
          and the whole grid overflows the page instead of scrolling inside
          its own pane. */}
      <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-4 lg:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)]">
        {/* left rail */}
        <div className="space-y-4">
          <Select value={mailboxId ?? ''} onValueChange={(v) => { setMailboxId(v); setThreadMailboxOverride(null); setSelectedThreadId(null) }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {mailboxes.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="flex items-center gap-2">
                    {m.displayName}
                    {m.unreadCount > 0 ? <Badge tone="info">{m.unreadCount}</Badge> : null}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <nav className="space-y-0.5">
            <button
              type="button"
              onClick={() => {
                setSection('folders')
                setView('all')
                setSelectedThreadId(null)
                setThreadMailboxOverride(null)
                setSelectedThreadIds(new Set())
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                section === 'folders' && view === 'all'
                  ? 'bg-primary-subtle text-primary'
                  : 'text-foreground-muted hover:bg-surface-sunken hover:text-foreground',
              )}
            >
              <Layers className="size-4" />
              All mail
            </button>
            {FOLDERS.map((folder) => (
              <button
                key={folder.view}
                type="button"
                onClick={() => {
                  setSection('folders')
                  setView(folder.view)
                  setSelectedThreadId(null)
                  setThreadMailboxOverride(null)
                  setSelectedThreadIds(new Set())
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  section === 'folders' && view === folder.view
                    ? 'bg-primary-subtle text-primary'
                    : 'text-foreground-muted hover:bg-surface-sunken hover:text-foreground',
                )}
              >
                <folder.icon className="size-4" />
                {folder.label}
              </button>
            ))}
            <div className="my-1 border-t border-border" />
            <button
              type="button"
              onClick={() => {
                setSection('attachments')
                setSelectedThreadId(null)
                setSelectedThreadIds(new Set())
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                section === 'attachments'
                  ? 'bg-primary-subtle text-primary'
                  : 'text-foreground-muted hover:bg-surface-sunken hover:text-foreground',
              )}
            >
              <Paperclip className="size-4" />
              Attachments
            </button>
          </nav>
        </div>

        {/* middle pane: folder list, or the Attachments tab */}
        {section === 'attachments' ? (
          mailboxId ? (
            <AttachmentsPanel
              mailboxId={mailboxId}
              onOpenThread={(threadId) => {
                setSection('folders')
                setThreadMailboxOverride(null)
                setSelectedThreadId(threadId)
              }}
            />
          ) : null
        ) : (
          <div className="min-w-0 space-y-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isAll ? 'Search all mailboxes…' : 'Search this mailbox…'}
            />

            <DataTable
              data={conversationItems}
              columns={columns}
              isLoading={conversationsLoading}
              empty={
                <EmptyState
                  icon={MailOpen}
                  title="Nothing here"
                  description={isAll ? 'No mail in Inbox, Starred, Sent or Drafts.' : 'This folder is empty.'}
                />
              }
              onRowClick={(row) => {
                const rowMailboxId = row.mailboxId ?? mailboxId
                setThreadMailboxOverride(row.mailboxId ?? null)
                if (can('mail:send') && row.messageCount === 1 && row.lastStatus === 'draft') {
                  void openDraft(row.threadId, rowMailboxId)
                  return
                }
                setSelectedThreadId(row.threadId)
              }}
              getRowId={(row) => (row.mailboxId ? `${row.mailboxId}:${row.threadId}` : row.threadId)}
              selectedIds={selectedThreadIds}
              onToggleRow={(id) =>
                setSelectedThreadIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                })
              }
              onToggleAll={(ids) =>
                setSelectedThreadIds((prev) => (prev.size === ids.length ? new Set() : new Set(ids)))
              }
              infinite={{
                total: conversationTotal,
                hasMore: Boolean(hasMoreConversations),
                isFetchingMore: isFetchingMoreConversations,
                onLoadMore: () => void fetchNextConversations(),
              }}
            />

            <BulkActionBar count={selectedThreadIds.size} onClear={() => setSelectedThreadIds(new Set())}>
              {view === 'archive' || view === 'junk' || view === 'trash' ? (
                <Button variant="ghost" size="sm" onClick={() => bulk.mutate('inbox')}>Move to inbox</Button>
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => bulk.mutate('read')}>Read</Button>
              <Button variant="ghost" size="sm" onClick={() => bulk.mutate('archive')}>Archive</Button>
              {view === 'junk' ? (
                <Button variant="ghost" size="sm" onClick={() => bulk.mutate('unjunk')}>Not junk</Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => bulk.mutate('junk')}>Junk</Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => bulk.mutate('trash')}>Trash</Button>
            </BulkActionBar>
          </div>
        )}

        {/* thread pane */}
        <div className="hidden min-w-0 overflow-hidden rounded-lg border border-border bg-surface lg:block">
          {selectedThreadId && threadMailboxId ? (
            <ThreadPane
              mailboxId={threadMailboxId}
              threadId={selectedThreadId}
              mailboxes={mailboxes}
              onReply={(state) => {
                setComposeInitial(state)
                setComposeOpen(true)
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-8">
              <EmptyState icon={MailIcon} title="Select a conversation" description="Pick one from the list to read it." />
            </div>
          )}
        </div>
      </div>

      {/* small screens: thread opens as a dialog instead of a third column.
          Gated on isLargeScreen, not just a `lg:hidden` class — the dialog's
          own darkened overlay has no such class and would otherwise still
          cover the page (with the desktop third column showing through
          underneath it) whenever a thread is selected on a wide screen. */}
      {!isLargeScreen ? (
        <Dialog open={Boolean(selectedThreadId)} onOpenChange={(open) => { if (!open) setSelectedThreadId(null) }}>
          <DialogContent className="max-w-2xl">
            {selectedThreadId && threadMailboxId ? (
              <ThreadPane
                mailboxId={threadMailboxId}
                threadId={selectedThreadId}
                mailboxes={mailboxes}
                onReply={(state) => {
                  setComposeInitial(state)
                  setComposeOpen(true)
                }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}

      {mailboxId ? (
        <ComposeDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          mailboxes={mailboxes}
          initial={composeInitial}
          onSent={() => {
            void queryClient.invalidateQueries({ queryKey: ['mail'] })
          }}
        />
      ) : null}

      {can('mail:manage') ? (
        <ManageMailboxesDialog open={manageOpen} onOpenChange={setManageOpen} />
      ) : null}
    </Page>
  )
}
